import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Form, InputGroup, Spinner, Dropdown } from 'react-bootstrap';
import { formatWithoutCurrency } from '../../helpers/helperFunction';
import validator from 'validator';
import { postRequest } from '../../helpers/apiHandler';
import config from '../../config/Constant';
import { reloadTopbar } from '../../store/actions/reduxAction';
import CustomAlert from '../CustomAlert';
import CloseIcon from '../../layouts/SVG/CloseIcon';

const PerkesoTransferCreditModel = ({
  show,
  showTransferModal,
  onClose,
  getCreditData,
  creditData,
  memberList,
}) => {
  const dispatch = useDispatch();
  const { access_token, user } = useSelector((state) => state.auth);
  const { user_id, user_name, email, role } = creditData;
  
  const [loadingSend, setLoadingSend] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [creditAmount, setCreditAmount] = useState('');
  const [searchAccount, setSearchAccount] = useState('');

  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  
  const [fromAccount, setFromAccount] = useState('');
  const [toAccount, setToAccount] = useState('');

  const [senderUserId, setSenderUserId] = useState('');
  const [senderUserName, setSenderUserName] = useState('');

  const [receiverUserId, setReceiverUserId] = useState('');
  const [receiverUserName, setReceiverUserName] = useState('');
  
  const [creditError, setCreditError] = useState('');
  const [transferError, setTransferError] = useState('');
  const [alertState, setAlertState] = useState({ show: false, variant: 'danger', message: '' });

  useEffect(() => {
    if (show || showTransferModal) {
      resetForm();
    }
  }, [show, showTransferModal]);

  const filteredUsers = memberList.filter((user) =>
    `${user.role} ${user.user_name} ${user.email}`.includes(searchAccount.toLowerCase())
  );

  const resetForm = () => {
    if (showTransferModal) {
      setFromAccount('');
      setToAccount('');
      setCurrentBalance('');
      setSenderUserId('');
      setSenderUserName('');
      setReceiverUserId('');
      setReceiverUserName('');
    } else {
      setFromAccount(`${user.role} | ${user.name} | ${user.email}`);
      setToAccount(`${user.role} | ${user.name} | ${user.email}`);
      setCurrentBalance(user.available_balance || 0);
      setSenderUserId(user.id);
      setSenderUserName(user.name);
      setReceiverUserId(user_id);
      setReceiverUserName(user_name);
    }
    setCreditAmount('');
    setCreditError('');
    setTransferError('');
  };

  const handleAmountChange = (e) => {
    setCreditAmount(e.target.value);
    setCreditError('');
  };

  const handleSelectSender = (e) => {
    const selectedValue = e.target.value;
    setFromAccount(selectedValue);

    const fromAccount = memberList.find(
      (account) => `${account.role} | ${account.user_name} | ${account.email}` === selectedValue
    );

    if (fromAccount) {
      setSenderUserId(fromAccount.user_id);
      setSenderUserName(fromAccount.user_name);
      setCurrentBalance(fromAccount.available_balance || 0);

      if (fromAccount.user_name === receiverUserName) {
        setTransferError('Credit transfer to own account is not allowed.');
      } else {
        setTransferError('');
      }
    }
    setSearchAccount('');
  };

  const handleSelectReceiver = (e) => {
    const selectedValue = e.target.value;
    setToAccount(selectedValue);

    const toAccount = memberList.find(
      (account) => `${account.role} | ${account.user_name} | ${account.email}` === selectedValue
    );

    if (toAccount) {
      setReceiverUserId(toAccount.user_id);
      setReceiverUserName(toAccount.user_name);

      if (toAccount.user_name === senderUserName) {
        setTransferError('Credit transfer to own account is not allowed.');
      } else {
        setTransferError('');
      }
    }
    setSearchAccount('');
  };

  const handleTransferCredit = (e) => {
    e.preventDefault();

    if (!validator.isNumeric(creditAmount) || creditAmount <= 0) {
      setCreditError('Please enter a valid credit amount.');
      return;
    }

    if (senderUserId === receiverUserId) {
      setTransferError('Credit transfer to own account is not allowed.');
      return;
    }

    if (currentBalance < creditAmount) {
      setTransferError('Insufficient credits to transfer.');
      return;
    }

    setLoadingSend(true);
    const data = {
      sender_user_id: senderUserId,
      receiver_user_id: receiverUserId,
      credit_amount: creditAmount,
    };

    postRequest('/admin/transfer-credit', data, access_token.token, (res) => {
      setLoadingSend(false);
      if (res.success) {
        setAlertState({
          show: true,
          message: 'Credits transferred successfully!',
          variant: 'success',
        });
        getCreditData();
        onClose();
      } else {
        setAlertState({
          show: true,
          message: 'There was an issue while transferring credits',
          variant: 'danger',
        });
      }
      dispatch(reloadTopbar());
    });
  };

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <>
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, message: '', variant: 'danger' })}
      />
      <Modal className="model-transfer-balance" show={show} onHide={onClose} centered>
        <Modal.Body>
          <CloseIcon handleModalClose={onClose} />
          <Modal.Title as="h4" className="modal-title mb-3">
            Transfer Credit
          </Modal.Title>

          <Form.Group>
            <Form.Label>Credit Balance: {currentBalance}</Form.Label>
          </Form.Group>

          <Form.Group>
            <Form.Label>From Account:</Form.Label>
            <Dropdown show={showFromDropdown} onToggle={(isOpen) => setShowFromDropdown(isOpen)}>
              <Dropdown.Toggle
                variant="ghost"
                id="from-account-dropdown"
                className="dropdown-toggle"
              >
                {fromAccount || 'Select Account'}
              </Dropdown.Toggle>

              <Dropdown.Menu className="p-3 dropdown-menu">
                <Form.Control
                  type="text"
                  placeholder="Search by role, username, email"
                  value={searchAccount}
                  onChange={(e) => setSearchAccount(e.target.value)}
                  className="mb-2 form-control"
                />

                {filteredUsers.map((data, index) => (
                  <Dropdown.Item
                    key={index}
                    onClick={() => {
                      handleSelectSender({
                        target: { value: `${data.role} | ${data.user_name} | ${data.email}` },
                      });
                      setShowFromDropdown(false);
                    }}
                  >
                    {data.role} | {data.user_name} | {data.email}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          {showTransferModal ? (
            <Form.Group>
              <Form.Label>To Account:</Form.Label>
              <Dropdown show={showToDropdown} onToggle={(isOpen) => setShowToDropdown(isOpen)}>
                <Dropdown.Toggle
                  variant="ghost"
                  id="to-account-dropdown"
                  className="dropdown-toggle"
                >
                  {toAccount || 'Select Account'}
                </Dropdown.Toggle>

                <Dropdown.Menu className="p-3 dropdown-menu">
                  <Form.Control
                    type="text"
                    placeholder="Search by role, username, email"
                    value={searchAccount}
                    onChange={(e) => setSearchAccount(e.target.value)}
                    className="mb-2 form-control"
                  />

                  {filteredUsers.map((data, index) => (
                    <Dropdown.Item
                      key={index}
                      onClick={() => {
                        handleSelectReceiver({
                          target: { value: `${data.role} | ${data.user_name} | ${data.email}` },
                        });
                        setShowToDropdown(false);
                      }}
                    >
                      {data.role} | {data.user_name} | {data.email}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          ) : (
            <Form.Group>
              <Form.Label>To Account:</Form.Label>
              <Form.Control
                type="text"
                value={`${role} | ${user_name} | ${email}`}
                readOnly
                className="form-control"
              />
            </Form.Group>
          )}

          <Form.Group>
            <Form.Label>Credit Amount:</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type="number"
                value={creditAmount}
                onChange={handleAmountChange}
                isInvalid={!!creditError}
                placeholder="Enter Amount"
                className="form-control"
              />
              <Form.Control.Feedback type="invalid">{creditError}</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          {creditAmount && !transferError && (
            <Form.Group className="notice-success">
              Confirm transfer of <strong>{formatWithoutCurrency(creditAmount, 0, 0)}</strong>{' '}
              credits from <strong>{senderUserName}</strong> to <strong>{receiverUserName}</strong>.
            </Form.Group>
          )}

          {transferError && <Form.Group className="notice-danger">{transferError}</Form.Group>}

          <div className="d-flex justify-content-left mt-3 gap-3">
            <Button
              variant="primary"
              onClick={handleTransferCredit}
              disabled={loadingSend}
              style={{ minWidth: '160px' }}
            >
              {loadingSend && <Spinner animation="border" size="sm" className="mr-2" />}
              Transfer Credit
            </Button>
            <Button variant="ghost" onClick={onClose} style={{ minWidth: '160px' }}>
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PerkesoTransferCreditModel;
